import React, { useState, useEffect } from 'react';
import SearchBar from './components/SearchBar';
import WeatherDisplay from './components/WeatherDisplay';
import { getCurrentWeather } from './services/weatherService';
import './App.css';

/**
 * Main App Component
 * Demonstrates:
 * - useState for state management
 * - useEffect for lifecycle methods
 * - Props passing to child components
 * - API integration
 * - Error handling
 */
function App() {
  // State management using useState hook
  const [weatherData, setWeatherData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [currentCity, setCurrentCity] = useState('');

  /**
   * useEffect hook - Fetches weather for default city on component mount
   * Demonstrates: Component lifecycle (componentDidMount equivalent)
   */
  useEffect(() => {
    // Fetch weather for Toronto on initial load
    fetchWeather('Toronto');
  }, []); // Empty dependency array means this runs once on mount

  /**
   * Fetches weather data for a given city
   * Demonstrates: Async/await, error handling, state updates
   * @param {string} city - City name to search for
   */
  const fetchWeather = async (city) => {
    try {
      setLoading(true);
      setError(null);

      const data = await getCurrentWeather(city);
      setWeatherData(data);
      setCurrentCity(city);
    } catch (err) {
      setError(err.message);
      setWeatherData(null);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Handles search submission from SearchBar component
   * Demonstrates: Event handling, callback props
   * @param {string} city - City name from search input
   */
  const handleSearch = (city) => {
    fetchWeather(city);
  };

  return (
    <div className="App">
      {/* Animated background */}
      <div className="background-animation">
        <div className="cloud cloud1"></div>
        <div className="cloud cloud2"></div>
        <div className="cloud cloud3"></div>
      </div>

      <div className="container">
        <header className="app-header">
          <div className="header-content">
            <div className="logo-section">
              <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="logo-icon">
                <path d="M18 10h-1.26A8 8 0 1 0 9 20h9a5 5 0 0 0 0-10z"/>
              </svg>
              <h1 className="app-title">Weather App</h1>
            </div>
            <p className="app-subtitle">Student ID: 10148758 | COMP3123 Lab Test 2</p>
          </div>
        </header>

        <main className="app-main">
          {/* SearchBar component - demonstrates props usage */}
          <SearchBar onSearch={handleSearch} />

          {/* WeatherDisplay component - demonstrates props and conditional rendering */}
          <WeatherDisplay
            weatherData={weatherData}
            loading={loading}
            error={error}
          />
        </main>

        <footer className="app-footer">
          <p>
            Powered by <a href="https://openweathermap.org/" target="_blank" rel="noopener noreferrer">OpenWeatherMap API</a>
          </p>
          <p className="footer-note">
            Built with React | {new Date().getFullYear()}
          </p>
        </footer>
      </div>
    </div>
  );
}

export default App;
